import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from '@tanstack/react-router';
import {
  Banner, Button, Card, DataTable, Dialog, EmptyState, Select, Skeleton, TopAppBar, useSnackbar,
} from '@meshble/molo';
import { api, must, mustOk } from '../lib/api';
import { useServers } from '../lib/dati';
import { usePermessi } from '../session';
import { BarraUtente } from '../shell';
import { Anello, CaricoSezione } from '../brand';
import { Globe } from '../icons';
import { useCredenzialiCloud, useIPs } from './server-cloud';
import { t } from '../lib/lingua';
import { ConPermesso } from '../permesso';

/**
 * Gli indirizzi fissi dell'organizzazione: gli Additional IP dei suoi
 * progetti OVH, come li vede OVH adesso, accostati ai server dell'hub.
 *
 * Un IP fisso segue il servizio e non la macchina: in una DR passa al server
 * nuovo e nessun DNS va toccato — né i nostri record né quelli che i clienti
 * puntano da soli. Da qui si vede a chi è, si assegna, si sposta, si libera.
 * Si compra dal pannello di OVH: passa da un'API che la chiave dell'azienda
 * non copre, e addebita il conto.
 *
 * Chi vede i server vede anche gli IP; assegnarli è di owner e admin, come le
 * credenziali, perché spostare un IP sposta il traffico di un cliente.
 */
export function IPPage() {
  const { slug } = useParams({ from: '/shell/orgs/$slug/ip' });
  const navigate = useNavigate();
  const qc = useQueryClient();
  const avvisa = useSnackbar();
  // Il permesso e non il ruolo: dal blocco 33 spostare e comprare IP è
  // dell'owner, e un admin vedeva i pulsanti per prendere un 403.
  const permessi = usePermessi(slug);

  const ips = useIPs(slug);
  const servers = useServers(slug);
  const credenziali = useCredenzialiCloud(slug);
  const [daAssegnare, setDaAssegnare] = useState<string | null>(null);
  const haProgetto = (credenziali.data?.credentials ?? []).some((c) => c.provider === 'ovh');
  const elenco = ips.data?.ips ?? [];

  const libera = useMutation({
    mutationFn: (ip: string) => mustOk(api.DELETE('/api/v1/orgs/{slug}/ips/{ip}/server', { params: { path: { slug, ip } } })),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['ips', slug] });
      void qc.invalidateQueries({ queryKey: ['servers', slug] });
      avvisa(t('IP liberato: il server smette di rispondere su di lui in un minuto'));
    },
    onError: (e) => avvisa((e as Error).message),
  });

  return (
    <>
      <TopAppBar title={t('Indirizzi IP')} subtitle={t('Gli IP fissi del progetto: seguono il servizio, non la macchina')} actions={<BarraUtente slug={slug} />} />
      <CaricoSezione attiva={ips.isPending} />
      <div className="md-content">
        {ips.error && <Banner tone="error" title={t('OVH non risponde')}>{ips.error.message}</Banner>}
        {credenziali.isSuccess && !haProgetto && (
          <EmptyState
            icon={<Globe />}
            title={t('Nessun progetto OVH collegato')}
            action={<Button variant="filled" onClick={() => navigate({ to: '/orgs/$slug/impostazioni', params: { slug } })}>{t('Vai a Impostazioni')}</Button>}
          >
            {t('Gli IP fissi sono gli Additional IP del progetto Public Cloud: si vedono qui quando le chiavi del progetto sono collegate.')}
          </EmptyState>
        )}
        {ips.isPending && haProgetto && <Skeleton height="6rem" />}
        {ips.isSuccess && haProgetto && elenco.length === 0 && (
          <EmptyState icon={<Globe />} title={t('Nessun Additional IP nel progetto')}>
            {t('Si compra dal pannello di OVH: Public Cloud → Network → Public IPs → Additional IP, nella zona dei tuoi server (GRA, SBG e RBX stanno insieme). Costa pochi euro al mese e comparirà qui.')}
          </EmptyState>
        )}
        {elenco.length > 0 && (
          <Card variant="outlined">
            <DataTable
              columns={[
                { key: 'ip', header: t('Indirizzo') },
                { key: 'paese', header: t('Paese') },
                { key: 'server', header: 'Server' },
                { key: 'ovh', header: t('Su OVH instradato a') },
                { key: 'azioni', header: '' },
              ]}
              rowKey={(r) => String(r.ip)}
              rows={elenco.map((ip) => ({
                ip: <span className="md-mono">{ip.ip}</span>,
                paese: ip.country || '—',
                server: ip.serverId
                  ? <Button variant="text" onClick={() => navigate({ to: '/orgs/$slug/servers/$id', params: { slug, id: ip.serverId! } })}>{ip.serverName}</Button>
                  : <span style={{ color: 'var(--md-sys-color-on-surface-variant)' }}>{t('libero')}</span>,
                ovh: ip.pending ? t('in spostamento…') : ip.attachedTo || t('nessuno'),
                azioni: (
                  <span className="md-stack__inline">
                    <ConPermesso ha={permessi} serve="ip.manage">
                      <Button variant="tonal" disabled={ip.pending} onClick={() => setDaAssegnare(ip.ip)}>
                        {ip.serverId ? t('Sposta') : t('Assegna')}
                      </Button>
                    </ConPermesso>
                    {ip.serverId && (
                      <ConPermesso ha={permessi} serve="ip.manage">
                        <Button variant="text" disabled={libera.isPending} onClick={() => libera.mutate(ip.ip)}>{t('Libera')}</Button>
                      </ConPermesso>
                    )}
                  </span>
                ),
              }))}
            />
          </Card>
        )}
        {elenco.length > 0 && (
          <p className="md-body-small" style={{ margin: 0, color: 'var(--md-sys-color-on-surface-variant)' }}>
            {t('«Server» è chi ce l’ha per Molo: i nomi delle sue istanze risolvono lì. «Su OVH instradato a» è dove OVH lo manda adesso: dopo una DR può essere ancora la macchina persa finché il server nuovo non lo prende. «Paese» è la geolocalizzazione scelta comprandolo; gli spostamenti restano dentro la zona dei datacenter (GRA, SBG e RBX insieme).')}
          </p>
        )}
      </div>
      {daAssegnare && (
        <AssegnaDialog
          slug={slug}
          ip={daAssegnare}
          attuale={elenco.find((i) => i.ip === daAssegnare)?.serverId}
          servers={(servers.data?.servers ?? []).filter((s) => s.status === 'ready')}
          onClose={() => setDaAssegnare(null)}
        />
      )}
    </>
  );
}

function AssegnaDialog({
  slug, ip, attuale, servers, onClose,
}: { slug: string; ip: string; attuale?: string; servers: Array<{ id: string; name: string; provider: string }>; onClose: () => void }) {
  const qc = useQueryClient();
  const avvisa = useSnackbar();
  const navigate = useNavigate();
  const candidati = servers.filter((s) => s.id !== attuale);
  const [serverId, setServerId] = useState(candidati[0]?.id ?? '');
  const scelto = candidati.find((s) => s.id === serverId);

  const assegna = useMutation({
    mutationFn: () => must(api.PUT('/api/v1/orgs/{slug}/ips/{ip}/server', { params: { path: { slug, ip } }, body: { serverId } })),
    onSuccess: (srv) => {
      void qc.invalidateQueries({ queryKey: ['ips', slug] });
      void qc.invalidateQueries({ queryKey: ['servers', slug] });
      onClose();
      avvisa(t('{ip} va su {server}: segui il passo in Attività', { ip, server: srv.name }));
      if (srv.jobId) navigate({ to: '/orgs/$slug/jobs/$id', params: { slug, id: srv.jobId } });
    },
  });

  return (
    <Dialog
      open
      onOpenChange={(v) => { if (!v) onClose(); }}
      title={attuale ? t('Sposta {ip}', { ip }) : t('Assegna {ip}', { ip })}
      description={t('Molo sposta l’IP presso OVH, lo configura sulla macchina e fa risolvere lì i nomi delle istanze del server. Serve un server pronto.')}
      actions={
        <>
          <Button variant="text" onClick={onClose}>{t('Annulla')}</Button>
          <Button variant="filled" icon={assegna.isPending ? <Anello /> : undefined} disabled={!serverId || assegna.isPending} onClick={() => assegna.mutate()}>
            {assegna.isPending ? t('Sposto…') : attuale ? t('Sposta') : t('Assegna')}
          </Button>
        </>
      }
    >
      <div className="md-form">
        {assegna.error && <Banner tone="error" title={t('Non assegnato')}>{(assegna.error as Error).message}</Banner>}
        {candidati.length === 0 ? (
          <Banner tone="error" title={t('Nessun server pronto')}>{t('Un IP fisso si assegna a un server pronto, o alla creazione di uno nuovo.')}</Banner>
        ) : (
          <Select
            label="Server"
            value={serverId}
            options={candidati.map((s) => ({ value: s.id, label: s.name }))}
            onChange={(e) => setServerId(e.target.value)}
            help={scelto?.provider === 'custom_ssh'
              ? t('Macchina collegata a mano: Molo la configura, ma lo spostamento presso OVH lo fai tu dal pannello (Public IPs → Additional IP → Update attached instance).')
              : t('Dev’essere nella stessa zona dell’IP: OVH non lo sposta altrove.')}
          />
        )}
        {attuale && (
          <Banner tone="info" title={t('Il server che ce l’ha lo perde')}>
            {t('I nomi delle sue istanze tornano sull’indirizzo della macchina, e chi le raggiunge da questo IP arriva sul server nuovo.')}
          </Banner>
        )}
      </div>
    </Dialog>
  );
}
